(function () {
  let productIndexPromise = null;

  function text(value) {
    return String(value || '');
  }

  function escapeHtml(value) {
    return text(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalize(value) {
    return text(value)
      .toLowerCase()
      .replace(/[\u0591-\u05C7]/g, '')
      .replace(/[׳']/g, '')
      .replace(/[״"]/g, '')
      .replace(/[־–—_]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeSize(size) {
    return text(size).toLowerCase().replace(/\s+/g, '').replace(/\+/g, '');
  }

  const FIT_SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  function clamp(number, min, max) {
    return Math.max(min, Math.min(max, number));
  }

  function confidenceFromDistance(distance) {
    const cleanDistance = clamp(Number(distance) || 0, 0, 0.5);
    const confidence = Math.round(clamp(94 - cleanDistance * 18, 84, 96));
    const secondConfidence = Math.round(clamp(76 + cleanDistance * 14, 68, confidence - 7));

    return { confidence: confidence, secondConfidence: secondConfidence };
  }

  function estimateFinderSize(height, weight, age, fitPreference) {
    let score = (Number(weight) - 52) / 11 + (Number(height) - 168) / 14;

    if (Number(age) >= 45) score += 0.12;
    if (Number(age) >= 60) score += 0.12;
    if (fitPreference === 'tight') score -= 0.42;
    if (fitPreference === 'loose') score += 0.48;

    const index = clamp(Math.round(score), 0, FIT_SIZE_ORDER.length - 1);
    const exact = clamp(score, 0, FIT_SIZE_ORDER.length - 1);
    const distance = Math.abs(exact - index);
    const confidencePair = confidenceFromDistance(distance);

    let secondIndex = exact > index ? index + 1 : index - 1;
    secondIndex = clamp(secondIndex, 0, FIT_SIZE_ORDER.length - 1);
    if (secondIndex === index) secondIndex = clamp(index + 1, 0, FIT_SIZE_ORDER.length - 1);

    return {
      size: FIT_SIZE_ORDER[index],
      confidence: confidencePair.confidence,
      secondSize: FIT_SIZE_ORDER[secondIndex],
      secondConfidence: confidencePair.secondConfidence
    };
  }

  function sizeLabelForButton(size) {
    return size === '3XL' ? '3XL+' : size;
  }

  function sizeButtonSelector(size) {
    return '[data-rsf-size=\"' + String(size).replace(/\"/g, '') + '\"]';
  }

  function sizeTokens(value) {
    return text(value)
      .toLowerCase()
      .replace(/\+/g, '')
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  function productHasSelectedSize(product, sizes) {
    if (!sizes.length) return true;
    const productSizes = Array.isArray(product.sz) ? product.sz.map(normalizeSize) : [];
    return sizes.some(function (size) {
      const cleanSize = normalizeSize(size);
      if (cleanSize === '3xl') return productSizes.includes('3xl') || productSizes.includes('xxxl');
      return productSizes.includes(cleanSize);
    });
  }

  function variantMatchesSize(variant, sizes) {
    if (!sizes.length) return true;
    const tokens = []
      .concat(sizeTokens(variant.title))
      .concat(sizeTokens(variant.option1))
      .concat(sizeTokens(variant.option2))
      .concat(sizeTokens(variant.option3))
      .concat(Array.isArray(variant.options) ? variant.options.flatMap(sizeTokens) : []);

    return sizes.some(function (size) {
      const cleanSize = normalizeSize(size);
      if (cleanSize === '3xl') return tokens.includes('3xl') || tokens.includes('xxxl');
      return tokens.includes(cleanSize);
    });
  }

  async function loadProductIndex(url) {
    if (!productIndexPromise) {
      productIndexPromise = fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Product index failed');
          return response.json();
        })
        .then(function (data) {
          return Array.isArray(data.products) ? data.products : [];
        });
    }
    return productIndexPromise;
  }

  function isHeroProduct(product) {
    const productType = normalize(product.ty);
    return productType === 'heros' || productType === 'heroes';
  }

  const SEARCH_ALIASES = {
    'leo messi': ['messi', 'lionel messi', 'מסי', 'ליאו מסי', 'ליונל מסי'],
    'lionel messi': ['messi', 'leo messi', 'מסי', 'ליאו מסי', 'ליונל מסי'],
    'thierry henry': ['henry', 'תיירי הנרי', 'הנרי'],
    'zlatan ibrahimovic': ['ibrahimovic', 'zlatan', 'איברהימוביץ', 'זלאטן'],
    'ronaldo nazario': ['ronaldo', 'r9', 'רונאלדו', 'רונאלדו נזאריו'],
    'cristiano ronaldo': ['ronaldo', 'cr7', 'כריסטיאנו רונאלדו', 'רונאלדו'],
    'david beckham': ['beckham', 'בקהאם', 'דייויד בקהאם'],
    'ronaldinho gaucho': ['ronaldinho', 'רונאלדיניו'],
    'zinedine zidane': ['zidane', 'זידאן', 'זינדין זידאן'],
    'diego maradona': ['maradona', 'מראדונה', 'דייגו מראדונה'],
    'roberto baggio': ['baggio', 'באג׳יו', 'באגיו'],
    'roberto carlos': ['r. carlos', 'r.carlos', 'carlos', 'רוברטו קרלוס'],
    'ruud van nistelrooy': ['v. nistelrooy', 'v.nistelrooy', 'van nistelrooy', 'van nilsterooy', 'ואן ניסטלרוי'],
    'robin van persie': ['v.persie', 'van persie', 'persie', 'ואן פרסי'],
    'andres iniesta': ['a. iniesta', 'iniesta', 'אינייסטה', 'אנדרס אינייסטה'],
    'sergio ramos': ['ramos', 'סרחיו ראמוס', 'ראמוס'],
    'kun aguero': ['aguero', 'agüero', 'אגוארו', 'קון אגוארו'],
    'kylian mbappe': ['mbappe', 'אמבפה', 'קיליאן אמבפה'],
    'mohamed salah': ['m.salah', 'salah', 'סלאח', 'מוחמד סלאח'],
    'frank lampard': ['lampard', 'למפארד', 'פרנק למפארד'],
    'steven gerrard': ['gerrard', 'ג׳רארד', 'סטיבן ג׳רארד'],
    'wayne rooney': ['rooney', 'רוני', 'ויין רוני'],
    'eric cantona': ['cantona', 'קנטונה', 'אריק קנטונה'],
    'alessandro del piero': ['del piero', 'דל פיירו', 'אלסנדרו דל פיירו'],
    'francesco totti': ['totti', 'טוטי', 'פרנצ׳סקו טוטי'],
    'andriy shevchenko': ['shevchenko', 'שבצ׳נקו', 'אנדריי שבצ׳נקו'],
    'paolo maldini': ['maldini', 'מאלדיני', 'פאולו מאלדיני'],
    'kaka': ['kaká', 'קאקה'],
    'luis figo': ['figo', 'פיגו', 'לואיש פיגו'],
    'didier drogba': ['drogba', 'דרוגבה', 'דידייה דרוגבה'],
    'dennis bergkamp': ['bergkamp', 'ברגקאמפ', 'דניס ברגקאמפ'],
    'robert pires': ['pires', 'pirès', 'פירס', 'רובר פירס'],
    'patrick vieira': ['vieira', 'ויירה', 'פטריק ויירה'],
    'neymar jr': ['neymar', 'ניימאר', 'ניימאר ג׳וניור']
  };

  function expandedQueries(query) {
    const clean = normalize(query);
    if (!clean) return [];
    const values = [clean];
    Object.keys(SEARCH_ALIASES).forEach(function (key) {
      const normalizedKey = normalize(key);
      const aliases = SEARCH_ALIASES[key].map(normalize);
      if (clean === normalizedKey || aliases.includes(clean)) {
        values.push(normalizedKey);
        aliases.forEach(function (alias) { values.push(alias); });
      }
    });
    return values.filter(function (value, index, arr) { return value && arr.indexOf(value) === index; });
  }

  function words(value) {
    return normalize(value).split(/[^a-z0-9\u05d0-\u05ea]+/).filter(Boolean);
  }

  function fieldMatches(fieldValue, query, allowReverse) {
    const field = normalize(fieldValue);
    const queries = expandedQueries(query);
    if (!field || !queries.length) return false;

    return queries.some(function (singleQuery) {
      if (field === singleQuery || field.includes(singleQuery)) return true;
      if (allowReverse && singleQuery.includes(field)) return true;

      const queryWords = words(singleQuery);
      if (queryWords.length > 1 && queryWords.every(function (word) { return field.includes(word); })) return true;

      const fieldWords = words(field);
      if (allowReverse && fieldWords.length && fieldWords.every(function (word) { return singleQuery.includes(word); })) return true;

      return false;
    });
  }

  function scoreProduct(product, state) {
    const heroProduct = isHeroProduct(product);

    if (state.method === 'player') {
      if (!heroProduct) return -1;
    } else if (heroProduct) {
      return -1;
    }

    if (state.sport && product.sp && product.sp !== state.sport) return -1;
    if (state.audience === 'kids' && !product.k) return -1;
    if (!productHasSelectedSize(product, state.sizes)) return -1;

    const query = normalize(state.query);
    const era = normalize(state.era);
    const method = state.method;
    const haystack = normalize([product.t, product.e, product.h, product.s, product.ty, product.tm, product.tmh, product.co, product.coh, product.hr, product.hrh].join(' '));
    const team = normalize([product.tm, product.tmh].join(' '));
    const country = normalize([product.co, product.coh].join(' '));
    const heroes = normalize([product.hr, product.hrh].join(' '));
    const title = normalize([product.t, product.e].join(' '));

    let score = 0;
    let matchedMainQuery = !query;

    if (!query && !era) score += 1;

    if (query) {
      if (method === 'team') {
        if (fieldMatches(team, query, false)) { score += team === query ? 130 : 110; matchedMainQuery = true; }
        if (fieldMatches(title, query, false)) { score += 60; matchedMainQuery = true; }
        if (fieldMatches(haystack, query, false)) { score += 30; matchedMainQuery = true; }
      } else if (method === 'country') {
        if (fieldMatches(country, query, false)) { score += country === query ? 130 : 110; matchedMainQuery = true; }
        if (fieldMatches(haystack, query, false)) { score += 30; matchedMainQuery = true; }
      } else if (method === 'player') {
        if (fieldMatches(heroes, query, true)) { score += heroes === query ? 140 : 120; matchedMainQuery = true; }
        if (fieldMatches(title, query, true)) { score += 60; matchedMainQuery = true; }
        if (fieldMatches(haystack, query, true)) { score += 25; matchedMainQuery = true; }
      } else if (method === 'era') {
        if (fieldMatches(title, query, false)) { score += 90; matchedMainQuery = true; }
        if (fieldMatches(haystack, query, false)) { score += 50; matchedMainQuery = true; }
      } else if (fieldMatches(haystack, query, false)) {
        score += 40;
        matchedMainQuery = true;
      }

      if (!matchedMainQuery) return -1;
    }

    if (era) {
      if (fieldMatches(title, era, false)) score += 60;
      else if (fieldMatches(haystack, era, false)) score += 35;
      else return -1;
    }

    if (product.i) score += 4;
    if (Array.isArray(product.sz) && product.sz.length) score += 2;
    if (product.ty === 'Football Shirt' || product.ty === 'Heros') score += 3;
    if (state.method === 'player' && product.hr) score += 8;

    return score;
  }

  function shuffleArray(items) {
    const shuffled = items.slice();
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      const current = shuffled[index];
      shuffled[index] = shuffled[randomIndex];
      shuffled[randomIndex] = current;
    }
    return shuffled;
  }

  function getMatchingProducts(products, state) {
    const scored = [];
    const used = {};

    products.forEach(function (product) {
      if (!product || !product.h || used[product.h]) return;
      const score = scoreProduct(product, state);
      if (score < 0) return;
      used[product.h] = true;
      scored.push({ product: product, score: score, random: Math.random() });
    });

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.random - b.random;
    });

    return scored.map(function (item) { return item.product; });
  }

  async function fetchProductByHandle(handle) {
    const response = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return null;
    return response.json();
  }

  function cleanPersonalization(personalization) {
    const enabled = personalization && personalization.enabled;
    const customName = text(personalization && personalization.name).trim();
    const customNumber = text(personalization && personalization.number).trim();
    const properties = {};

    if (!enabled) return properties;
    if (customName) properties['שם'] = customName;
    if (customNumber) properties['מספר'] = customNumber;

    return properties;
  }

  async function addProductToCart(product, sizes, button, onAdded, personalization) {
    button.textContent = 'מוסיף לסל...';
    button.disabled = true;

    try {
      const fullProduct = await fetchProductByHandle(product.h);
      const variants = fullProduct && Array.isArray(fullProduct.variants) ? fullProduct.variants : [];
      const selectedVariant = variants.find(function (variant) {
        return variant.available && variantMatchesSize(variant, sizes);
      }) || variants.find(function (variant) { return variant.available; });

      if (!selectedVariant) throw new Error('No variant');

      const properties = cleanPersonalization(personalization);
      const payload = { id: selectedVariant.id, quantity: 1 };
      if (Object.keys(properties).length) payload.properties = properties;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Cart add failed');

      button.textContent = 'נוסף לסל';
      button.classList.add('is-added');
      if (typeof onAdded === 'function') onAdded(product);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'פתח מוצר';
      button.onclick = function () {
        window.location.href = '/products/' + product.h;
      };
    }
  }


  async function fetchLiveSearchProducts(query) {
    const q = String(query || '').trim();
    if (!q) return [];
    try {
      const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const response = await fetch(rootPath + 'search?view=regista-live-json&type=product&q=' + encodeURIComponent(q), {credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
      if (!response.ok) return [];
      const json = await response.json();
      return Array.isArray(json.products) ? json.products : [];
    } catch (error) { return []; }
  }

  function mergeFinderProducts(live, indexed) {
    const seen = new Set();
    return [].concat(live || [], indexed || []).filter(function (product) {
      if (!product || !product.h || seen.has(product.h)) return false;
      seen.add(product.h); return true;
    });
  }
  function initFinder(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const modal = root.querySelector('[data-rsf-modal]');
    const openButton = root.querySelector('[data-rsf-open]');
    const closeButton = root.querySelector('[data-rsf-close]');
    const steps = root.querySelectorAll('[data-rsf-step]');
    const sectionId = root.getAttribute('data-section-id') || '';
    const config = window.RegistaShirtFinderConfig && window.RegistaShirtFinderConfig[sectionId]
      ? window.RegistaShirtFinderConfig[sectionId]
      : {};

    const state = {
      sport: '',
      audience: '',
      sizes: [],
      method: '',
      query: '',
      era: '',
      allResults: [],
      renderedCount: 0,
      addedCount: 0,
      fit: {
        height: 0,
        weight: 0,
        age: 0,
        preference: 'regular',
        recommendedSize: ''
      },
      personalization: {
        enabled: false,
        name: '',
        number: ''
      }
    };

    function goToStep(stepNumber) {
      steps.forEach(function (step) { step.classList.remove('is-active'); });
      const nextStep = root.querySelector('[data-rsf-step="' + stepNumber + '"]');
      if (nextStep) nextStep.classList.add('is-active');
      if (closeButton) closeButton.style.color = stepNumber === '0' || stepNumber === 0 ? '#fff' : '#111';

      const shell = root.querySelector('.regista-finder-shell');
      if (shell) shell.scrollTop = 0;
    }

    function syncSizeButtons() {
      root.querySelectorAll('[data-rsf-size]').forEach(function (button) {
        const size = button.getAttribute('data-rsf-size');
        button.classList.toggle('is-selected', state.sizes.includes(size));
      });
    }

    function chooseSingleSize(size) {
      state.sizes = size ? [size] : [];
      syncSizeButtons();
    }

    let previousBodyOverflow = '';
    let returnFocus = null;
    function openModal() {
      if (!modal.classList.contains('is-open')) {
        previousBodyOverflow = document.body.style.overflow;
        returnFocus = document.activeElement;
      }
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      goToStep(0);
      window.setTimeout(function(){ if(closeButton && closeButton.focus) closeButton.focus(); },20);
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = previousBodyOverflow;
      if (returnFocus && returnFocus.focus) returnFocus.focus();
      returnFocus = null;
    }

    function updateDynamicText(method) {
      const title = root.querySelector('[data-rsf-dynamic-title]');
      const subtitle = root.querySelector('[data-rsf-dynamic-subtitle]');
      const input = root.querySelector('[data-rsf-query]');

      const texts = {
        team: { title: 'איזו קבוצה?', subtitle: 'כתוב מועדון או נבחרת', placeholder: 'לדוגמה: ארסנל, ברזיל, ברצלונה' },
        country: { title: 'איזו מדינה?', subtitle: 'נמצא לך חולצות מהמדינה שבחרת', placeholder: 'לדוגמה: ברזיל, איטליה, אנגליה' },
        player: { title: 'איזה שחקן?', subtitle: 'כתוב את שם השחקן שאתה מחפש', placeholder: 'לדוגמה: מסי, רונאלדו, בקהאם' },
        era: { title: 'איזו תקופה?', subtitle: 'כתוב עונה או עשור', placeholder: 'לדוגמה: 1998, 2000s, 2004' }
      };

      const current = texts[method] || texts.team;
      if (title) title.textContent = current.title;
      if (subtitle) subtitle.textContent = current.subtitle;
      if (input) input.placeholder = current.placeholder;
    }

    function renderSelection() {
      const box = root.querySelector('[data-rsf-selection-box]');
      if (!box) return;

      const sportText = state.sport === 'basketball' ? 'כדורסל' : 'כדורגל';
      const audienceMap = { men: 'גברים', women: 'נשים', kids: 'ילדים' };
      const methodMap = { team: 'לפי קבוצה', country: 'לפי מדינה', player: 'לפי שחקן', era: 'לפי תקופה' };

      box.innerHTML =
        '<strong>ענף:</strong> ' + escapeHtml(sportText) + '<br>' +
        '<strong>קהל:</strong> ' + escapeHtml(audienceMap[state.audience] || '') + '<br>' +
        '<strong>מידות:</strong> ' + escapeHtml(state.sizes.join(', ') || 'לא נבחר') + '<br>' +
        '<strong>סוג חיפוש:</strong> ' + escapeHtml(methodMap[state.method] || '') + '<br>' +
        '<strong>חיפוש:</strong> ' + escapeHtml(state.query || '') +
        (state.era ? '<br><strong>תקופה:</strong> ' + escapeHtml(state.era) : '');
    }

    function updateResultActions() {
      const showMore = root.querySelector('[data-rsf-show-more]');
      const cartButton = root.querySelector('[data-rsf-cart]');
      const count = root.querySelector('[data-rsf-count]');

      if (showMore) {
        showMore.classList.toggle('is-active', state.renderedCount < state.allResults.length);
      }

      if (cartButton) {
        cartButton.classList.toggle('is-active', state.addedCount > 0);
      }

      if (count) {
        count.textContent = state.allResults.length ? 'מציג ' + state.renderedCount + ' מתוך ' + state.allResults.length + ' התאמות' : '';
      }
    }

    function getPersonalization() {
      const toggle = root.querySelector('[data-rsf-custom-toggle]');
      const nameInput = root.querySelector('[data-rsf-custom-name]');
      const numberInput = root.querySelector('[data-rsf-custom-number]');

      state.personalization.enabled = !!(toggle && toggle.checked);
      state.personalization.name = nameInput ? nameInput.value.trim() : '';
      state.personalization.number = numberInput ? numberInput.value.trim() : '';

      return state.personalization;
    }

    function renderProducts(products, append) {
      const grid = root.querySelector('[data-rsf-results-grid]');
      const empty = root.querySelector('[data-rsf-empty]');
      const loader = root.querySelector('[data-rsf-loader]');
      if (!grid || !empty) return;

      if (!append) grid.innerHTML = '';
      if (loader) loader.classList.remove('is-active');

      if (!products.length && !append) {
        empty.classList.add('is-active');
        updateResultActions();
        return;
      }

      empty.classList.remove('is-active');

      products.forEach(function (product) {
        const card = document.createElement('div');
        card.className = 'regista-product-card';

        const title = product.t || product.e || '';
        const displayTitle = '\u200F' + title;
        const imageHtml = product.i ? '<img src="' + escapeHtml(product.i) + '" alt="' + escapeHtml(title) + '" loading="lazy">' : '';
        const sizesHtml = Array.isArray(product.sz) && product.sz.length
          ? '<div class="regista-product-sizes">מידות זמינות: ' + escapeHtml(product.sz.join(', ')) + '</div>'
          : '';

        card.innerHTML = imageHtml + '<h3 dir="rtl"><span dir="rtl">' + escapeHtml(displayTitle) + '</span></h3>' + sizesHtml + '<button type="button">מצאתי לי חולצה</button>';
        const button = card.querySelector('button');
        button.addEventListener('click', function () {
          addProductToCart(product, state.sizes, button, function () {
            state.addedCount += 1;
            updateResultActions();
          }, getPersonalization());
        });
        grid.appendChild(card);
      });

      updateResultActions();
    }

    function renderNextResults() {
      const resultsLimit = Number(config.resultsLimit || 6);
      const nextProducts = state.allResults.slice(state.renderedCount, state.renderedCount + resultsLimit);
      state.renderedCount += nextProducts.length;
      renderProducts(nextProducts, state.renderedCount > nextProducts.length);
    }

    async function searchProducts() {
      const grid = root.querySelector('[data-rsf-results-grid]');
      const empty = root.querySelector('[data-rsf-empty]');
      const loader = root.querySelector('[data-rsf-loader]');
      if (!grid || !empty) return;

      state.allResults = [];
      state.renderedCount = 0;
      state.addedCount = 0;
      grid.innerHTML = '';
      empty.classList.remove('is-active');
      updateResultActions();
      if (loader) loader.classList.add('is-active');

      try {
        const loaded = await Promise.all([
          loadProductIndex(config.productIndexUrl || '/assets/regista-product-index.json').catch(function () { return []; }),
          fetchLiveSearchProducts(state.query || state.era || '')
        ]);
        const products = mergeFinderProducts(loaded[1], loaded[0]);
        state.allResults = getMatchingProducts(products, state);
        renderNextResults();
      } catch (error) {
        if (loader) loader.classList.remove('is-active');
        empty.classList.add('is-active');
        updateResultActions();
      }
    }

    if (openButton) openButton.addEventListener('click', openModal);
    if (closeButton) closeButton.addEventListener('click', closeModal);

    root.querySelectorAll('[data-rsf-next]').forEach(function (button) {
      button.addEventListener('click', function () { goToStep(button.getAttribute('data-rsf-next')); });
    });

    root.querySelectorAll('[data-rsf-field]').forEach(function (button) {
      button.addEventListener('click', function () {
        const field = button.getAttribute('data-rsf-field');
        const value = button.getAttribute('data-rsf-value');
        state[field] = value;

        root.querySelectorAll('[data-rsf-field="' + field + '"]').forEach(function (item) { item.classList.remove('is-selected'); });
        button.classList.add('is-selected');

        if (field === 'sport') goToStep(2);
        if (field === 'audience') goToStep(3);
      });
    });

    root.querySelectorAll('[data-rsf-size]').forEach(function (button) {
      button.addEventListener('click', function () {
        const size = button.getAttribute('data-rsf-size');
        if (state.sizes.includes(size)) {
          state.sizes = state.sizes.filter(function (item) { return item !== size; });
        } else {
          state.sizes.push(size);
        }
        syncSizeButtons();
      });
    });

    const fitHeightInput = root.querySelector('[data-rsf-fit-height]');
    const fitWeightInput = root.querySelector('[data-rsf-fit-weight]');
    const fitAgeInput = root.querySelector('[data-rsf-fit-age]');

    function validateFitStart() {
      const nextButton = root.querySelector('[data-rsf-fit-next]');
      if (!nextButton || !fitHeightInput || !fitWeightInput) return;
      const height = Number(fitHeightInput.value);
      const weight = Number(fitWeightInput.value);
      nextButton.disabled = !(height >= 120 && height <= 230 && weight >= 35 && weight <= 180);
    }

    function validateFitAge() {
      const nextButton = root.querySelector('[data-rsf-fit-age-next]');
      if (!nextButton || !fitAgeInput) return;
      const age = Number(fitAgeInput.value);
      nextButton.disabled = !(age >= 12 && age <= 100);
    }

    if (fitHeightInput) fitHeightInput.addEventListener('input', validateFitStart);
    if (fitWeightInput) fitWeightInput.addEventListener('input', validateFitStart);
    if (fitAgeInput) fitAgeInput.addEventListener('input', validateFitAge);

    const fitStart = root.querySelector('[data-rsf-fit-start]');
    if (fitStart) {
      fitStart.addEventListener('click', function () {
        goToStep(31);
      });
    }

    const fitNext = root.querySelector('[data-rsf-fit-next]');
    if (fitNext) {
      fitNext.addEventListener('click', function () {
        state.fit.height = Number(fitHeightInput ? fitHeightInput.value : 0);
        state.fit.weight = Number(fitWeightInput ? fitWeightInput.value : 0);
        goToStep(32);
      });
    }

    const fitAgeNext = root.querySelector('[data-rsf-fit-age-next]');
    if (fitAgeNext) {
      fitAgeNext.addEventListener('click', function () {
        state.fit.age = Number(fitAgeInput ? fitAgeInput.value : 0);
        goToStep(33);
      });
    }

    root.querySelectorAll('[data-rsf-fit-pref]').forEach(function (button) {
      button.addEventListener('click', function () {
        root.querySelectorAll('[data-rsf-fit-pref]').forEach(function (item) { item.classList.remove('is-selected'); });
        button.classList.add('is-selected');
        state.fit.preference = button.getAttribute('data-rsf-fit-pref') || 'regular';
      });
    });

    const fitCalculate = root.querySelector('[data-rsf-fit-calculate]');
    if (fitCalculate) {
      fitCalculate.addEventListener('click', function () {
        const result = estimateFinderSize(state.fit.height, state.fit.weight, state.fit.age, state.fit.preference);
        state.fit.recommendedSize = result.size;

        const sizeEl = root.querySelector('[data-rsf-fit-result-size]');
        const bars = root.querySelector('[data-rsf-fit-bars]');
        const copy = root.querySelector('[data-rsf-fit-copy]');
        const choose = root.querySelector('[data-rsf-fit-choose]');

        if (sizeEl) sizeEl.textContent = sizeLabelForButton(result.size);
        if (choose) choose.textContent = 'בחר מידה ' + sizeLabelForButton(result.size) + ' וחזור לסינון';
        if (copy) copy.innerHTML = 'לפי הגובה, המשקל והעדפת הגזרה שבחרת, מידה <strong>' + escapeHtml(sizeLabelForButton(result.size)) + '</strong> נראית כמו הבחירה הנוחה ביותר עבורך בחולצת כדורגל.';
        if (bars) {
          bars.innerHTML =
            '<div class="regista-fit-finder-bar"><div class="regista-fit-finder-bar-head"><span>' + escapeHtml(sizeLabelForButton(result.size)) + '</span><span>' + result.confidence + '%</span></div><div class="regista-fit-finder-track"><div class="regista-fit-finder-fill" style="width:' + result.confidence + '%"></div></div></div>' +
            '<div class="regista-fit-finder-bar regista-fit-finder-bar--secondary"><div class="regista-fit-finder-bar-head"><span>' + escapeHtml(sizeLabelForButton(result.secondSize)) + '</span><span>' + result.secondConfidence + '%</span></div><div class="regista-fit-finder-track"><div class="regista-fit-finder-fill" style="width:' + result.secondConfidence + '%"></div></div></div>';
        }

        goToStep(34);
      });
    }

    const fitChoose = root.querySelector('[data-rsf-fit-choose]');
    if (fitChoose) {
      fitChoose.addEventListener('click', function () {
        chooseSingleSize(state.fit.recommendedSize);
        goToStep(3);
      });
    }

    const fitRestart = root.querySelector('[data-rsf-fit-restart]');
    if (fitRestart) {
      fitRestart.addEventListener('click', function () {
        state.fit.height = 0;
        state.fit.weight = 0;
        state.fit.age = 0;
        state.fit.preference = 'regular';
        state.fit.recommendedSize = '';
        if (fitHeightInput) fitHeightInput.value = '';
        if (fitWeightInput) fitWeightInput.value = '';
        if (fitAgeInput) fitAgeInput.value = '';
        root.querySelectorAll('[data-rsf-fit-pref]').forEach(function (item) { item.classList.remove('is-selected'); });
        const regular = root.querySelector('[data-rsf-fit-pref="regular"]');
        if (regular) regular.classList.add('is-selected');
        validateFitStart();
        validateFitAge();
        goToStep(31);
      });
    }

    const customToggle = root.querySelector('[data-rsf-custom-toggle]');
    if (customToggle) {
      customToggle.addEventListener('change', function () {
        const fields = root.querySelector('[data-rsf-custom-fields]');
        if (fields) fields.classList.toggle('is-active', customToggle.checked);
      });
    }

    root.querySelectorAll('[data-rsf-method]').forEach(function (button) {
      button.addEventListener('click', function () {
        state.method = button.getAttribute('data-rsf-method');
        updateDynamicText(state.method);
        goToStep(5);
      });
    });

    const eraExtra = root.querySelector('[data-rsf-era-extra]');
    if (eraExtra) {
      eraExtra.addEventListener('change', function () {
        const box = root.querySelector('[data-rsf-era-extra-box]');
        if (box) box.classList.toggle('is-active', this.checked);
      });
    }

    const searchButton = root.querySelector('[data-rsf-search]');
    const finalSearchButton = root.querySelector('[data-rsf-final-search]');
    if (searchButton && finalSearchButton) searchButton.addEventListener('click', function () { finalSearchButton.click(); });

    const queryInput = root.querySelector('[data-rsf-query]');
    if (queryInput && finalSearchButton) {
      queryInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') finalSearchButton.click();
      });
    }

    if (finalSearchButton) {
      finalSearchButton.addEventListener('click', function () {
        const query = root.querySelector('[data-rsf-query]');
        const eraInput = root.querySelector('[data-rsf-era]');
        const eraCheckbox = root.querySelector('[data-rsf-era-extra]');
        state.query = query ? query.value.trim() : '';
        state.era = eraInput && eraCheckbox && eraCheckbox.checked ? eraInput.value.trim() : '';
        renderSelection();
        goToStep(6);
        searchProducts();
      });
    }

    const showMore = root.querySelector('[data-rsf-show-more]');
    if (showMore) {
      showMore.addEventListener('click', function () {
        renderNextResults();
      });
    }

    const cartButton = root.querySelector('[data-rsf-cart]');
    if (cartButton) {
      cartButton.addEventListener('click', function () {
        window.location.href = config.cartRedirect || '/cart';
      });
    }

    const selectionToggle = root.querySelector('[data-rsf-selection-toggle]');
    if (selectionToggle) {
      selectionToggle.addEventListener('click', function () {
        const box = root.querySelector('[data-rsf-selection-box]');
        if (box) box.classList.toggle('is-open');
      });
    }

    const restart = root.querySelector('[data-rsf-restart]');
    if (restart) {
      restart.addEventListener('click', function () {
        state.sport = '';
        state.audience = '';
        state.sizes = [];
        state.method = '';
        state.query = '';
        state.era = '';
        state.allResults = [];
        state.renderedCount = 0;
        state.addedCount = 0;
        state.personalization.enabled = false;
        state.personalization.name = '';
        state.personalization.number = '';

        updateResultActions();
        root.querySelectorAll('.is-selected').forEach(function (item) { item.classList.remove('is-selected'); });
        const regularFit = root.querySelector('[data-rsf-fit-pref="regular"]');
        if (regularFit) regularFit.classList.add('is-selected');
        const query = root.querySelector('[data-rsf-query]');
        const era = root.querySelector('[data-rsf-era]');
        const eraCheckbox = root.querySelector('[data-rsf-era-extra]');
        const eraBox = root.querySelector('[data-rsf-era-extra-box]');
        const customToggleReset = root.querySelector('[data-rsf-custom-toggle]');
        const customFieldsReset = root.querySelector('[data-rsf-custom-fields]');
        const customNameReset = root.querySelector('[data-rsf-custom-name]');
        const customNumberReset = root.querySelector('[data-rsf-custom-number]');
        if (query) query.value = '';
        if (era) era.value = '';
        if (eraCheckbox) eraCheckbox.checked = false;
        if (eraBox) eraBox.classList.remove('is-active');
        if (customToggleReset) customToggleReset.checked = false;
        if (customFieldsReset) customFieldsReset.classList.remove('is-active');
        if (customNameReset) customNameReset.value = '';
        if (customNumberReset) customNumberReset.value = '';

        goToStep(1);
      });
    }

    if (modal) {
      modal.addEventListener('click', function (event) {
        if (event.target === modal) closeModal();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal && modal.classList.contains('is-open')) closeModal();
    });
  }

  function initAll() {
    document.querySelectorAll('[data-regista-shirt-finder]').forEach(initFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) event.target.querySelectorAll('[data-regista-shirt-finder]').forEach(initFinder);
  });
})();
